import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';
import LocationSphere from '../spheres/LocationSphere';
import { Colors } from '../../../data/Colors';
import DetailedSphere from '../spheres/DetailedSphere';
import useAspectScale from '../hooks/useAspectScale';
import useDetailSectionAnimation from '../../../hooks/useDetailSectionAnimation';
import { DetailSectionIDs } from '../../../data/Sections';
import { useAudioContext } from '../../../context/AudioContext';
import FocusPoints from '../../../data/FocusPoints';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import {SceneSection} from '../View3D';

const expectationPos = new Vector3(-0.83, 0.0, 0);
const realityPos = new Vector3(0.83, 0.0, 0);
const wife1Pos = new Vector3(-0.83 - 0.3, 0.05, -0.1);
const wife2Pos = new Vector3(0.83 - 0.3, 0.05, -0.1);

const husbandPos = new Vector3(-0.83 + 0.3, -0.05, 0.1);
const catPos = new Vector3(0.83 + 0.1, -0.3, 0.1);

const origin = new Vector3();

function updateOverlap(value: number, sphereRef: RefObject<Group>, wifeRef: RefObject<Group>)
{
  const sphere = sphereRef.current;
  const wife = wifeRef.current;

  if (!sphere || !wife) return;

  const viz = value > 0;
  sphere.visible = viz;
  wife.visible = viz;

  sphere.scale.set(value, value, value);
  wife.scale.set(value, value, value);
}

export default function DynamicBehavior({ groupProps, texts }: SceneSection): JSX.Element
{
  const scale = useAspectScale(1.4);
  const { playAudio } = useAudioContext();

  const { setShowExpectationBox, setShowRealityBox } = useDetailViewContext();
  const expectationSphereRef = useRef<Group>(null);
  const realSphereRef = useRef<Group>(null);
  const wife1Ref = useRef<Group>(null);
  const wife2Ref = useRef<Group>(null);
  const [ happiness, setHappiness ] = useState(-1.0);
  const [ winner, setWinner ] = useState(-1.0);
  const animProps = useMemo(() =>
  {
    return { reality: 0, expectation: 0, happiness: -1, winner: -1 };
  }, []);

  const resetAnimation = useCallback(() => {
    const sphere = realSphereRef.current;
    const wife = wife2Ref.current;
    if (!sphere || !wife) return;

    animProps.happiness = -1;
    animProps.winner = -1;
    animProps.reality = 0;
    animProps.expectation = 1;

    wife.visible = false;
    sphere.visible = false;

    setHappiness(animProps.happiness);
    setWinner(animProps.winner);

    updateOverlap(animProps.reality, realSphereRef, wife2Ref);
    updateOverlap(animProps.expectation, expectationSphereRef, wife1Ref);
  }, [animProps]);

  const animation = useDetailSectionAnimation(DetailSectionIDs.DecodingDynamicBehaviour, undefined, resetAnimation);

  useEffect(() =>
  {
    if (wife2Ref.current)
      wife2Ref.current.position.copy(wife2Pos);

    if (wife1Ref.current)
      wife1Ref.current.position.copy(wife1Pos);

    if (expectationSphereRef.current)
      expectationSphereRef.current.position.copy(expectationPos);

    if (realSphereRef.current)
      realSphereRef.current.position.copy(realityPos);

    // animate in reality
    animation.fromTo(animProps, { reality: 0 }, {
      reality: 0, duration: 1,
      immediateRender: false,
      onUpdate: () =>
      {
        updateOverlap(animProps.reality, realSphereRef, wife2Ref);
      }
    }, 0)
      .fromTo(animProps, { reality: 0 }, {
        reality: 1, duration: 1,
        immediateRender: false,
        onStart: () => {
          setShowRealityBox(true);
        },
        onUpdate: () =>
        {
          updateOverlap(animProps.reality, realSphereRef, wife2Ref);
        }
      }, 3)
    // fade out expectation
      .fromTo(animProps, { expectation: 1 }, {
        expectation: 0, duration: 0.3,
        immediateRender: false,
        onUpdate: () =>
        {
          updateOverlap(animProps.expectation, expectationSphereRef, wife1Ref);
        }
      }, 6)
    // flip winner vectors
      .fromTo(animProps, { winner: -1, happiness: -1 }, {
        winner: 1, happiness: 1, duration: 2,
        immediateRender: false,
        onUpdate: () =>
        {
          setWinner(animProps.winner);
          setHappiness(animProps.happiness);
        },
        onStart: () => {
          // since this plays randomly on different pages, we disabled it
          // playAudio('audioSphereRotation');
        }
      }, 7)
    // return to original state
      .fromTo(animProps, { happiness: 1, winner: 1, reality: 1, expectation: 0 }, {
        happiness: -1, winner: -1, reality: 0, duration: 1, expectation: 1,
        immediateRender: false,
        onStart: () => {
          setShowRealityBox(false);
        },
        onUpdate: () =>
        {
          setHappiness(animProps.happiness);
          setWinner(animProps.winner);

          updateOverlap(animProps.reality, realSphereRef, wife2Ref);
          updateOverlap(animProps.expectation, expectationSphereRef, wife1Ref);
        },
        onComplete: () =>
        {
          const sphere = realSphereRef.current;
          const wife = wife2Ref.current;
          if (!sphere || !wife) return;

          wife.visible = false;
          sphere.visible = false;
        }
      }, 11)
      .repeat(-1).repeatDelay(1);
    return () => gsap.killTweensOf(animProps);
  }, [animProps, animation, playAudio, setShowExpectationBox, setShowRealityBox]);

  const { activeDetailID, activeFocusID } = useDetailViewContext();

  useEffect(() => {
    if (activeFocusID === FocusPoints.DynamicBehaviorReality) {
      animation.pause(9);

      animProps.reality = 1;
      animProps.expectation = 0;
      animProps.happiness = 1;
      animProps.winner = 1;

      updateOverlap(animProps.reality, realSphereRef, wife2Ref);
      updateOverlap(animProps.expectation, expectationSphereRef, wife1Ref);

      setHappiness(animProps.happiness);
      setWinner(animProps.winner);

      setShowExpectationBox(true);
      setShowRealityBox(true);
    }
    else if (activeFocusID === FocusPoints.DynamicBehaviorExpectation) {
      animation.pause(2);
      animProps.reality = 0;
      animProps.expectation = 1;
      animProps.happiness = -1;
      animProps.winner = -1;

      updateOverlap(animProps.reality, realSphereRef, wife2Ref);
      updateOverlap(animProps.expectation, expectationSphereRef, wife1Ref);

      setHappiness(animProps.happiness);
      setWinner(animProps.winner);

      setShowExpectationBox(true);
      setShowRealityBox(false);
    }
    else if (activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour) {
      resetAnimation();
      animation.play(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFocusID, animProps, animation, setShowExpectationBox, setShowRealityBox]);

  return <group {...groupProps} scale={[ scale, scale, scale ]}
    visible={activeFocusID === FocusPoints.None || activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour}>

    <LocationSphere sphereCenter={origin} label={texts.home} sphereSize={0.6} azimuth={0.25} ref={expectationSphereRef}/>
    <LocationSphere sphereCenter={origin} label={texts.home} sphereSize={0.6} azimuth={0.25} ref={realSphereRef}/>

    <DetailedSphere label={texts.wife} color={Colors.WifeVector}
      sphereCenter={origin} sphereSize={0.27} arrowAngle={-1.6}
      happiness={1.0} winner={1.0} showHappy ref={wife1Ref}
      texts={texts}
    />

    <DetailedSphere label={texts.wife} color={Colors.WifeVector}
      sphereCenter={origin} sphereSize={0.27} arrowAngle={-1.6}
      happiness={1.0} winner={1.0} showHappy ref={wife2Ref}
      texts={texts}
    />

    <DetailedSphere label={texts.husband} color={Colors.HusbandVector}
      sphereCenter={husbandPos} sphereSize={0.27} arrowAngle={-1.6}
      happiness={-happiness} winner={-winner}
      showHappy showWinner
      texts={texts}
    />

    <DetailedSphere label={texts.cat} color={Colors.CatVector} arrowAngle={Math.PI * .5}
      sphereCenter={catPos} sphereSize={0.2}
      winner={winner} happiness={happiness} showHappy showWinner
      texts={texts}
    />
  </group>;
}
